import VideoEvidenceCard from 'components/Grading/video/VideoEvidenceCard';
import { useVideoReview } from 'components/Grading/video/VideoReviewContext';
import { useQuizReview } from 'components/QuizGrading/QuizReviewContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Stack, styled } from 'shell/components';
import Card from 'shell/components/Card';
import Text from 'shell/components/Text';
import { FileTypes } from 'utils/constants';
import { indexToLetters } from 'utils/helper';
import { isVideoFile, normalizeVideoEvidence } from 'utils/videoGrading';

const getViewerCategory = (fileType) => {
    if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return FileTypes.SHEET;
    }
    if (
        fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        fileType === 'application/vnd.ms-powerpoint'
    ) {
        return FileTypes.PRESENTATION;
    }
    if (fileType === 'application/pdf') {
        return FileTypes.PDF;
    }
    if (fileType === 'text/html') {
        return FileTypes.RICH_TEXT;
    }
    if (isVideoFile({ type: fileType })) return FileTypes.VIDEO;
    return null;
};

const SupportingEvidence = ({ locations, files, selectedFileId, setSelectedFileId }) => {
    const { t } = useTranslation('assignment');
    const videoReview = useVideoReview();
    const quizReview = useQuizReview();

    const [expanded, setExpanded] = useState(false);

    const currentFile = files.find(({ id }) => id === selectedFileId);

    const viewerCategory = getViewerCategory(currentFile?.type);
    const isClickable = viewerCategory !== FileTypes.SHEET;
    const hasLocations = locations?.length > 0;

    const handleAccordionChange = (_, isExpanded) => {
        setExpanded(isExpanded);
    };

    const renderText = (location, category = viewerCategory) => {
        if (typeof location === 'string') return location;
        switch (category) {
            case FileTypes.SHEET: {
                const { col, row } = location.highlight || {};
                const isInvalid = typeof col !== 'number' || typeof row !== 'number';
                if (isInvalid) return location.highlight;
                return t('grading-section.view-in-cell', 'View in Cell {{cell}}', {
                    cell: `${indexToLetters(col)}${row + 1}`,
                });
            }
            case FileTypes.PRESENTATION:
                return t('grading-section.view-in-slide', 'View in Slide {{slide}}', { slide: location.highlight + 1 });
            case FileTypes.PDF:
            case FileTypes.RICH_TEXT:
                return location.highlight;
            case FileTypes.VIDEO:
                return location.highlight;
            default:
                return quizReview ? (location.highlight ?? location.quote ?? location.reason ?? '') : '';
        }
    };

    if (!viewerCategory && !hasLocations) return null;

    return (
        <Stack sx={{ p: 3 }} spacing={2}>
            <Stack spacing={2}>
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                    <Text>
                        {viewerCategory === FileTypes.VIDEO
                            ? t('video-grading.evidence', 'Evidence')
                            : t('grading-section.supporting-evidence-label', 'Supporting Evidence')}
                    </Text>
                    {hasLocations && (
                        <ItemTextTag variant={'p3'} weight={'medium'}>
                            {t('item-count', '{{count}} items', { count: locations?.length })}
                        </ItemTextTag>
                    )}
                </Stack>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'space-between'} width={'100%'}>
                <SupportingEvidenceAccordion
                    disabled={!hasLocations}
                    expanded={hasLocations ? expanded : false}
                    onChange={handleAccordionChange}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SupportingEvidenceAccordionSummary>
                        <Text color={'inherit'}>
                            {expanded
                                ? t('grading-section.hide-locations-label', 'Hide Locations')
                                : t('grading-section.view-locations-label', 'View Locations')}
                        </Text>
                    </SupportingEvidenceAccordionSummary>
                    <SupportingEvidenceAccordionDetails>
                        {hasLocations &&
                            locations.map(({ value: location }, index) => {
                                if (!location) return null;
                                const file =
                                    files.find(({ id }) => id === location.file_id) ||
                                    (location.file_id == null && files.length === 1 ? files[0] : undefined);
                                const questionId = location.question_id ?? location.file_id;
                                const question = quizReview?.questions?.find((question) =>
                                    questionId != null
                                        ? String(question.id) === String(questionId)
                                        : question.criterion_ids?.includes(quizReview.selectedCriterionId)
                                );
                                const questionLabel = question
                                    ? t('quiz:grading.question-label', 'Q{{number}})', { number: question.position })
                                    : null;
                                if (isVideoFile(file) || (!file && viewerCategory === FileTypes.VIDEO)) {
                                    return (
                                        <VideoEvidenceCard
                                            key={index}
                                            evidence={normalizeVideoEvidence(location, file)}
                                            fileName={files.length > 1 ? file?.name : undefined}
                                            onSeek={videoReview?.seekToEvidence}
                                            onSelectFile={file ? () => setSelectedFileId(file.id) : undefined}
                                        />
                                    );
                                }
                                return (
                                    <CustomCard
                                        key={index}
                                        isClickable={isClickable}
                                        {...(question && {
                                            component: 'button',
                                            role: 'button',
                                            'aria-label': questionLabel,
                                        })}
                                        onClick={
                                            question
                                                ? () => quizReview.onSelectQuestion(question.id)
                                                : file
                                                  ? () => setSelectedFileId(file.id)
                                                  : undefined
                                        }
                                    >
                                        <LocationTag variant={'p3'} weight={'medium'}>
                                            {questionLabel ??
                                                file?.name ??
                                                location.file_id ??
                                                (quizReview
                                                    ? t('grading-section.location-unavailable', 'Location unavailable')
                                                    : '')}
                                        </LocationTag>
                                        <Text variant={'p3'}>
                                            {renderText(location, getViewerCategory(file?.type) || viewerCategory)}
                                        </Text>
                                    </CustomCard>
                                );
                            })}
                    </SupportingEvidenceAccordionDetails>
                </SupportingEvidenceAccordion>
                {!hasLocations && (
                    <StatusText>{t('grading-section.location-unavailable', 'Location unavailable')}</StatusText>
                )}
            </Stack>
        </Stack>
    );
};

const CustomCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'isClickable',
})(({ theme, isClickable }) => {
    return {
        alignItems: 'flex-start',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        boxShadow: 'none',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': {
            borderColor: !isClickable ? theme.palette.ensightful.neutral[300] : theme.palette.ensightful.primary[500],
            margin: !isClickable ? 'initial' : '-1px',
            borderWidth: !isClickable ? '1px' : '2px',
        },
    };
});

const ItemTextTag = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.primary[700],
        borderRadius: '40px',
    };
});

const LocationTag = styled(Text)(({ theme }) => {
    return {
        borderRadius: '40px',
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.ensightful.neutral[100],
    };
});

const StatusText = styled(Text)(({ theme }) => {
    return {
        width: '100%',
        textAlign: 'end',
        color: theme.palette.ensightful.error[600],
    };
});

const SupportingEvidenceAccordion = styled(Accordion)(({ theme }) => {
    return {
        width: '100%',
        background: 'inherit',

        '&.Mui-disabled': {
            backgroundColor: theme.palette.ensightful.shades[0],

            '.MuiAccordionSummary-root div': {
                color: theme.palette.ensightful.neutral[400],
            },
        },
    };
});

const SupportingEvidenceAccordionSummary = styled(AccordionSummary)(({ theme }) => {
    return {
        width: 'fit-content',
        '& > div': {
            color: theme.palette.ensightful.primary[500],
        },
    };
});

const SupportingEvidenceAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    };
});

export default SupportingEvidence;
